import React, { useState, useEffect, useRef, useContext } from 'react';
import { PostRequest, GetRequest } from 'components/common';
import { ContentPopup } from 'components/ContentPopup.jsx';
import QuoteBuilderForm from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderForm.jsx';
import SettingsTabs from 'components/layout_and_quote_builder/quote_builder/SettingsTabs.jsx';
import { ListenCondition } from 'components/layout_and_quote_builder/quote_builder/form_fields/index.js'
import { SelectedMachinesContext } from 'components/layout_and_quote_builder/quote_builder/selectedMachinesContext.js'
import UserUnitPriceTagSelect from 'components/layout_and_quote_builder/quote_builder/form_fields/UserUnitPriceTagSelect.jsx'
import { AskConfirm, ConfirmPopup } from 'components/ConfirmPopup.jsx'

// Which SVG-header column a machine price field feeds. Only these two keys name a machine above a dieline; every
// other price key (materials, lamination, ...) reports nothing.
const MACHINE_COLUMN_BY_PRICE_KEY = { printing_machine: 'printing', die_cutting_machine: 'die_cutting' };

const UserUnitPrice = ({ config, setValue, value, values, windowValue, disabled, setFetchInProgress, options, item, navSection, requestPublishItemOnChange, requestGetExtraItems, navSectionColumnType }) => {
  // A listen-condition captures fetchUserUnitPrices once at mount (its useEffect has [] deps), and the filter
  // clauses it re-runs read form values through v(). Reading `values` directly would bind to the MOUNT-time
  // object, so a refetch triggered later (e.g. on carton_gsm_changed) would re-filter against a stale GSM/colour.
  // Read through a ref refreshed every render, so v() always sees the current values even from that closure.
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const v = (key) => { return valuesRef.current[key] }
  const USER_UNIT_PRICE_CHANGED_WITH_ITEM_EVENT_KEY = 'quote_builder.user_unit_price_changed_with_item';
  const USER_UNIT_PRICE_ID_CHANGED_EVENT_KEY = '_user_unit_price_id_changed';

  const [allUserUnitPrices, setAllUserUnitPrices] = useState([]);
  const [userUnitPrices, setUserUnitPrices] = useState([]);
  // The picker-reset responder below is registered once ([] deps), so it must read the fetched list and the current
  // selection through refs -- both are empty/undefined at mount and only fill in after the list fetch resolves.
  const allUserUnitPricesRef = useRef(allUserUnitPrices);
  allUserUnitPricesRef.current = allUserUnitPrices;
  const valueRef = useRef(value);
  valueRef.current = value;
  const [configForPopUpForm, setConfigForPopUpForm] = useState({});
  const [selectedUnitPriceSummary, setSelectedUnitPriceSummary] = useState('');
  // The wastage this machine reported for the auto fire adeti, in its OWN sheet ("Ayar için kullanılan: 100
  // tabaka"), shown under the price description. It is the per-contributor value from the section's wastage
  // breakdown (window.material_auto_wastages on load, the material_wastage_recomputed event live), matched to this
  // machine's field -- so every reporting machine shows what it contributed, alongside the converted total the
  // material section shows. null for a machine that reports no wastage (materials, guillotine, ...).
  const [reportedWastage, setReportedWastage] = useState(null);
  const newValueAddedID = useRef();
  const defaultSelectedOnLoad = useRef(false);
  const [editingMode, setEditingMode] = useState(false);
  const [listingMode, setListingMode] = useState(false);
  const [copyFromSystemMode, setCopyFromSystemMode] = useState(false);
  const [notAvailable, setNotAvailable] = useState(false);
  // The linked attribute's create-sub-form field definitions, used only by pickers that opt in
  // (config.option_tags_attribute_key) to render each option's tags. Undefined until fetched (and for every
  // non-opted picker); AttributePropertyTags renders nothing without it.
  const [optionTagsSubFormAttributes, setOptionTagsSubFormAttributes] = useState(undefined);
  // Pickers that opt in (config.option_tags_attribute_key) show the custom tag dropdown with each option's
  // property tags; the rest keep the native <select>.
  const customSelectStyleEnabled = config.option_tags_attribute_key != undefined;
  const VALUE_NOT_AVAILABLE = 'not-available'
  const VALUE_EMPTY = "";
  const VALUE_NEW = "new-attribute";
  const newUserPricePopupRef = useRef();
  const editUserPricePopupRef = useRef();
  const listUserPricePopupRef = useRef();
  const confirmPopupRef = useRef({});
  const [notAvailableReason, setNotAvailableReason] = useState('');

  const [readOnly, setReadOnly] = useState(false);
  const [readOnlyMessage, setReadOnlyMessage] = useState('');

  // A filter clause can reference a section option that has not loaded yet (options arrive from an async
  // /quote-builder/sections fetch, so options.printing_machine_accepted_printing_types is undefined until
  // it does), and eval'ing the clause then throws. Fail CLOSED on that -- treat the row as not passing --
  // exactly as the filtered-out-reason display already does (firstFilteredOutReason). Without this the throw
  // aborts the whole filter, so the fetch callback never sets the filtered list and the dropdown freezes on
  // a stale, unfiltered one.
  const clausePasses = (user_unit_price, clause) => {
    try { return eval(clause); } catch(evaluationError) { return false; }
  }

  const filteredData = (data, filter_eval) => {
    if(config.filter != undefined && filter_eval == undefined) {
      data = data.filter((user_unit_price) => clausePasses(user_unit_price, config.filter))
    }

    if(config.filters != undefined && filter_eval == undefined) {
      for(let i=0; i< config.filters.length; i++) {
        data = data.filter((user_unit_price) => clausePasses(user_unit_price, config.filters[i].filter))
      }
    }

    if(filter_eval != undefined) {
      data = data.filter((user_unit_price) => clausePasses(user_unit_price, filter_eval))
    }

    return data;
  }

  // The last dynamic filter a refetch_user_unit_prices_and_filter listen condition applied (coating material,
  // colour, ...). That filter is passed as an argument, not stored, so any later BARE refetch -- a popup close,
  // a cross-column change, a window-value change -- would drop it and re-show the whole, unfiltered list. A
  // picker filtered ONLY by that listener (no config.filter/filters, e.g. coating material) then shows every
  // price after such a refetch. Remembering it here lets those incidental refetches re-apply it. The intentional
  // bare refetch (refetch_user_unit_prices, which re-runs the picker's OWN config.filters) never passes one and
  // never stores one, so it keeps re-running config.filters as before.
  const lastFilterEval = useRef(undefined);

  const fetchUserUnitPrices = (filter_eval) => {
    if(disabled) { return };
    if(filter_eval != undefined) { lastFilterEval.current = filter_eval; }
    setFetchInProgress(true);
    GetRequest({
      url: `/quote-builder/user_unit_prices/list/section/${config.key}/${window.locale}`,
      callback: (data) => {
        setAllUserUnitPrices(data);

        setFetchInProgress(false);
        data = filteredData(data, filter_eval);

        setUserUnitPrices(data);
      }
    })
  };

  // Opt-in per picker (config.live_fetch). Several pickers can share one UserUnitPrice `key` across sections --
  // the metal-strip cut/crease/perforation/... fields all use `key: metal_strip`, and a price created or edited
  // from any one of them belongs to every other. Rather than wire each picker to react to every sibling's
  // change, a live_fetch picker simply refetches its list from the server whenever it is focused, so opening it
  // always shows the current prices. Defined per render, so it reads the live `disabled` (fetch no-ops while the
  // picker is gated) and re-applies any dynamic filter this picker last used (lastFilterEval).
  const refetchListOnFocusIfLive = () => {
    if(!config.live_fetch) { return; }
    fetchUserUnitPrices(lastFilterEval.current);
  };

  // Keyed on `disabled`, not just mount: a section can mount disabled and un-gate later -- the material-price
  // gate greys every other section until the material is priced, then clears. With [] deps this one-shot fetch
  // was skipped at mount and never ran again, leaving the picker's list empty (no options, saved price lost)
  // once the section became active. Re-running when `disabled` clears loads the list on un-gate.
  useEffect(() => {
    if(disabled) { return };

    fetchUserUnitPrices();
  },[disabled]);

  // Re-filter the already-fetched list when a value it filters on lands ASYNCHRONOUSLY from another section
  // (e.g. the selected material's printing_types, fetched into material_printing_types after the material-change
  // event). A plain refetch -- carton_gsm's synchronous path -- would filter against the stale value, so re-filter
  // here instead. Opt-in per picker via config.refilter_on_value_change (a single value name).
  useEffect(() => {
    if(disabled || config.refilter_on_value_change == undefined || allUserUnitPrices.length == 0) { return; }
    checkForValueInUserPricesRequired.current = true;
    setUserUnitPrices(filteredData(allUserUnitPrices, lastFilterEval.current));
  }, [config.refilter_on_value_change != undefined ? values[config.refilter_on_value_change] : undefined]);

  // An EMPTY refilter value (e.g. a material that supports NO printing type) matches nothing, so the filtered
  // list is genuinely empty -- not the async options-load race the [userUnitPrices] re-validation fails closed
  // on. Reset a stranded saved selection to not-available, or it stays stored and priced behind an all-filtered
  // picker. Keyed on the list (and value) so it fires whether the list emptied from a LIVE material change or
  // from the section UN-GATING (its one-shot fetch runs the clause after the value already landed while gated).
  useEffect(() => {
    if(disabled || config.refilter_on_value_change == undefined) { return; }
    const refilterValue = values[config.refilter_on_value_change];
    if(Array.isArray(refilterValue) && refilterValue.length == 0 &&
       allUserUnitPrices.length > 0 && userUnitPrices.length == 0 &&
       config.not_available_enabled && value != VALUE_NOT_AVAILABLE && value != undefined && value != VALUE_EMPTY) {
      setValueToNotAvailable();
    }
  }, [userUnitPrices, value]);

  // Tell the SVG header above which machine THIS price is set to, so it names the selected machine instead of
  // the manufacturing line picked in step 1. Fires on mount and whenever the selection or the loaded list
  // changes; reports null (no name shown) when nothing valid is selected. A no-op for non-machine price keys,
  // and off the pricing tab where nothing provides the context. Looks the selection up in allUserUnitPrices
  // (the unfiltered list) so a selected-but-filtered-out price still resolves.
  const reportSelectedMachine = useContext(SelectedMachinesContext);
  useEffect(() => {
    const column = MACHINE_COLUMN_BY_PRICE_KEY[config.key];
    // Guard on `disabled` like every fetch/effect here: a disabled field never loads the list, so reporting
    // would wrongly clear the header for a section that actually has a machine.
    if (disabled || !reportSelectedMachine || !column) { return; }

    const selected = allUserUnitPrices.find((price) => String(price.id) === String(value));
    reportSelectedMachine(column, selected
      ? { name: selected.selected_machine_name, typeLabel: selected.selected_machine_type_label }
      : null);
  }, [value, allUserUnitPrices, disabled]);

  // On unmount, clear this column's machine so a header whose price field has gone (e.g. a part_type change
  // dropping one machine column while the column card stays) does not keep naming a machine no longer there.
  useEffect(() => () => {
    const column = MACHINE_COLUMN_BY_PRICE_KEY[config.key];
    if (reportSelectedMachine && column) { reportSelectedMachine(column, null); }
  }, []);

  const valueTobeSetAfterFetch = useRef(null);

  useEffect(() => {
    if(!disabled && config.view_selected_summary) {
      updateSelectedUnitPriceSummary();
    }

    if(userUnitPrices.length == 0 && value != undefined && value != 'not-available') {
      valueTobeSetAfterFetch.current = value;
    }

  }, [value, userUnitPrices])

  useEffect(() => {
    if(valueTobeSetAfterFetch.current != null && userUnitPrices.length > 0) {
      const stashedValue = valueTobeSetAfterFetch.current;
      // Consume the stash once. It exists to carry the saved value across the single gap before the list
      // loads; re-applying it on EVERY later refetch is what let a filtered-out id come back after it had
      // been reset to not-available (an oscillation). And only re-apply a value that is actually selectable
      // now -- a value the filters exclude must not be resurrected, so selectionIsFilteredOut() can reset it.
      valueTobeSetAfterFetch.current = null;
      if(userUnitPrices.some((userUnitPrice) => userUnitPrice.id == stashedValue)) {
        setValue(config.name, (_p) => { return stashedValue })
        setValue('updated_at', () => { return Date.now() } );
      }
    }
  }, [userUnitPrices] )

  useEffect(() => {
    if(config.refetch_list_on_window_value_change) {
      fetchUserUnitPrices(lastFilterEval.current);
    }

  }, [windowValue]);

  const setValueToFirstAvailableOption = () => {
    setValue(config.name, userUnitPrices.length > 0 ? (p) => { return userUnitPrices[0].id } : (p) => { return VALUE_NOT_AVAILABLE });
    setValue('updated_at', () => { return Date.now() } );
  }

  const setValueToNotAvailable = () => {
    setValue(config.name,  (p) => { return VALUE_NOT_AVAILABLE });
    setValue('updated_at', () => { return Date.now() } );
  }

  // A saved selection can be a machine the current filters exclude (e.g. its thickness or sheet size no
  // longer fits the section). The <select> then shows "Not included" -- the stored id matches no selectable
  // option, so the browser falls back to the first one -- while the id is still stored and still priced: an
  // inconsistent state where the dropdown reads not-included but a result shows. Reconcile it by resetting a
  // stranded selection to not-available, so the result clears to match the dropdown. Gated to pickers that
  // filter AND offer not-available, and to a LIVE list (at least one option passed, so the filters are not
  // merely failing closed on options that have not loaded yet -- that guards a valid pick from being wiped
  // during the async-options race). filteredOutUnitPrices() is the fetched-but-filtered-out set, so a value
  // found there is a real machine the filters dropped, never an empty / not-available / new-attribute value.
  const selectionIsFilteredOut = () => {
    return !config.select_first_item_on_load &&
      config.not_available_enabled &&
      (config.filter != undefined || config.filters != undefined) &&
      userUnitPrices.length > 0 &&
      filteredOutUnitPrices().some((userUnitPrice) => userUnitPrice.id == value);
  }

  useEffect(() => {
    if(disabled) { return };

    if(newValueAddedID.current != undefined) {
      setValue(config.name,  (p) => { return newValueAddedID.current } );
      setValue('updated_at', ( ) => { return Date.now() } );
      return;
    }

    if(selectionIsFilteredOut()) {
      setValueToNotAvailable();
      checkForValueInUserPricesRequired.current = false;
      return;
    }

    // Only a filter clause that reads section OPTIONS can fail closed to an empty list while those options are
    // still loading -- they arrive from an async /quote-builder/sections fetch, so an `options.X` clause evals
    // false until then. There, an empty list is ambiguous, so resetting on it would wipe a still-valid pick during
    // the async-options race (the same guard selectionIsFilteredOut carries), and the flag stays set so
    // re-validation defers until a non-empty list arrives. A clause that reads only form values (v(...)), the nav
    // section, or the price row itself is always evaluable -- e.g. coating, filtered by
    // v('coating_material_type')/v('coating_gsm') -- so its empty list is a genuine no-match that MUST reset. So
    // gate the non-empty-list guard on whether any filter clause references options, not on merely having filters.
    const filterClauses = config.filters != undefined ? config.filters.map((clause) => clause.filter)
      : (config.filter != undefined ? [config.filter] : []);
    const emptyListMayBeFailClosed = filterClauses.some((clause) => clause.includes('options.'));
    if(checkForValueInUserPricesRequired.current && (userUnitPrices.length > 0 || !emptyListMayBeFailClosed) && value != VALUE_NOT_AVAILABLE && value != undefined && value != null) {
      let valueFound = false;
      for(let i=0; i< userUnitPrices.length; i++) {
        if(userUnitPrices[i].id == value) {
          valueFound = true;
        }
      }

      if(!valueFound && config.not_available_enabled) {
        setValueToNotAvailable();
        checkForValueInUserPricesRequired.current = false;
        return;
      }

      if(!valueFound && !config.not_available_enabled) {
        setValueToFirstAvailableOption();
        checkForValueInUserPricesRequired.current = false;
        return;
      }
    }

    if(config.select_first_item_on_load) {
      setValueToFirstAvailableOption();
      return;
    }

    // Issue #5186: when no price is saved yet, pre-select the one matching the default thickness
    // for the part's material/machine (config.select_default_on_load_eval, evaluated against each
    // user_unit_price + options). Done once; a saved selection (including 'not-available') is kept.
    if(config.select_default_on_load_eval && !defaultSelectedOnLoad.current && !window.user_quote_finalised && (value == undefined || value == VALUE_EMPTY) && userUnitPrices.length > 0) {
      for(let i=0; i< userUnitPrices.length; i++) {
        let user_unit_price = userUnitPrices[i];
        if(eval(config.select_default_on_load_eval)) {
          setValue(config.name, (p) => { return userUnitPrices[i].id });
          setValue('updated_at', () => { return Date.now() });
          defaultSelectedOnLoad.current = true;
          return;
        }
      }
    }

  },[userUnitPrices]);

  const onNewUserPricePopupClose = () => {
    callRequestPublishItemOnChange();
    fetchUserUnitPrices(lastFilterEval.current);
    fetchSystemUnitPrices(lastFilterEval.current);
    publishUserUnitPriceListReFetchRequired();
  }

  const publishUserUnitPriceListReFetchRequired = () => {
    document.dispatchEvent(new CustomEvent(USER_UNIT_PRICE_ID_CHANGED_EVENT_KEY, { detail: { id: value, key: config.key, name: config.name, navSectionColumnType: navSectionColumnType } } ));
  }

  useEffect(() => {
    let listener = (e) => {
      // A price list is shared by every picker with the same key, so a picker that creates or edits a price
      // must refresh the others. Skip only THIS picker's own event -- identified by column AND field name,
      // because two pickers can share a column (the lacquer section's front/back machine-price fields) and
      // must still refresh each other; column alone is not a unique picker identity.
      const isOwnEvent = e.detail.navSectionColumnType == navSectionColumnType && e.detail.name == config.name;
      if(e.detail.key == config.key && !isOwnEvent && e.detail.id != 'not-available') {
        fetchUserUnitPrices(lastFilterEval.current);
      }
    }

    document.addEventListener(USER_UNIT_PRICE_ID_CHANGED_EVENT_KEY,  listener);
    return () => { document.removeEventListener(USER_UNIT_PRICE_ID_CHANGED_EVENT_KEY, listener);}
  }, []);

  const newUserPricePopup = () => {
    if(Object.keys(configForPopUpForm).length == 0) return;
    let currentValue = value;
    let callback = (id) => {
      newValueAddedID.current = id;

      if(newValueAddedID.current != undefined) {
        onNewUserPricePopupClose();
      } else {
        setValueToNotAvailable();
      }

      newUserPricePopupRef.current.style.display = "none";
      checkForValueInUserPricesRequired.current = true;
      setEditingMode(false);
      setListingMode(false);
      setCopyFromSystemMode(false);
    }
    let valuesContext = {};

    if(config.create_context != undefined) {
      for (const [contextKey, contextValue] of Object.entries(config.create_context)) {
        valuesContext[contextKey] = eval(contextValue)
      }
    }
    // what is this context for? we can pass values that can be used by config.create_read_only_values or anything like that
    // QuoteBuilderForm.jsx looks like it does not use context if you read the codebase but its called from sections.yml
    let content = <QuoteBuilderForm config={configForPopUpForm} showingState="new" afterSave={callback} afterCancel={callback} readOnlyValues={config.create_read_only_values} context={valuesContext} options={options} navSection={navSection} />;
    let response = <ContentPopup myRef={newUserPricePopupRef} autoOpen={true} content={content} cancelButton={false} beforeClose={callback} revealWhenReady={true} />;

    return response;
  }

  const checkForValueInUserPricesRequired = useRef(false);

  const editPricePopup = () => {
    if(Object.keys(configForPopUpForm).length == 0) return;

    let callback = () => {
      setEditingMode(false);
      setListingMode(false);
      setCopyFromSystemMode(false);
      onNewUserPricePopupClose();
      setValue('updated_at', () => { return Date.now() } );
      callRequestPublishItemOnChange();
      editUserPricePopupRef.current.style.display = "none";
      publishUserUnitPriceListReFetchRequired();
      checkForValueInUserPricesRequired.current = true;
    }

    let content = <QuoteBuilderForm config={configForPopUpForm} showingState="edit" afterSave={callback} afterCancel={callback} editStateId={value} linkToSettings={true} />;
    let response = <ContentPopup myRef={editUserPricePopupRef} autoOpen={true} content={content} cancelButton={false} beforeClose={callback} revealWhenReady={true} />;

    return response;
  }

  const copyFromSystemPopup = () => {
    if(Object.keys(configForPopUpForm).length == 0) return;

    let callback = (id) => {
      setEditingMode(false);
      setListingMode(false);
      setCopyFromSystemMode(false);
      newValueAddedID.current = id;
      if(newValueAddedID.current != undefined) {
        onNewUserPricePopupClose();
      } else {
        setValueToNotAvailable();
      }

      if (editUserPricePopupRef.current) {
        editUserPricePopupRef.current.style.display = "none";
      }
      checkForValueInUserPricesRequired.current = true;
    }

    let content = <QuoteBuilderForm config={configForPopUpForm} showingState="copy_from_system" afterSave={callback} afterCancel={callback} editStateId={copyFromSystemId} linkToSettings={true} />;
    let response = <ContentPopup myRef={editUserPricePopupRef} autoOpen={true} content={content} cancelButton={false} beforeClose={callback} revealWhenReady={true} />;

    return response;
  }

  const listingModePopup = () => {
    if(Object.keys(configForPopUpForm).length == 0) return;

    let callback = () => {
      setEditingMode(false);
      setListingMode(false);
      setCopyFromSystemMode(false);
      onNewUserPricePopupClose();
      callRequestPublishItemOnChange();
      listUserPricePopupRef.current.style.display = "none";
      checkForValueInUserPricesRequired.current = true;
    }

    let content = <><br /><SettingsTabs
      config={configForPopUpForm}
      showingState="list"
      afterSave={callback}
      afterCancel={callback}
      editStateId={value}
      hideDeleteFromList={false}
    /></>;

    let response = <ContentPopup myRef={listUserPricePopupRef} autoOpen={true} content={content} cancelButton={false} beforeClose={callback} />;

    return response;
  };

  const listAllItems = (e) => {
    e.preventDefault();
    setListingMode(true);
  }

  const handleEdit = (e) => {
    setEditingMode(true);
    e.preventDefault();
  }

  // Keyed on `disabled` for the same reason the list fetch above is (a section can mount gated and un-gate later,
  // and a []-deps mount fetch would never re-run). What is specific here: while this was skipped configForPopUpForm
  // stayed {}, and the edit / show-all-items popups bail on `Object.keys(configForPopUpForm).length == 0`, so those
  // links did nothing once the section un-gated.
  useEffect(() => {
    if(disabled) { return };
    if(config.create_enabled || config.edit_selected_item_enabled) {
      setFetchInProgress(true);
      GetRequest({
        url: `/quote-builder/config/settings/user_unit_prices/${config.key}`,
        useCache: true,
        callback: (data) => {
          setFetchInProgress(false);
          setConfigForPopUpForm(data);
        }
      })
    }
  }, [disabled]);

  // Opt-in material pickers show each option's material-type tags (printing methods + finishing
  // capabilities). Fetch that type's field definitions once, the same endpoint and shape the settings
  // list and the library picker use (create_sub_form.attributes), so the tags read identically. The
  // per-option tag VALUES ride along on each serialized price row as option_tags_properties.
  useEffect(() => {
    if(!customSelectStyleEnabled) { return };

    GetRequest({
      url: `/quote-builder/user_unit_price_attributes/config/${config.key}/${config.option_tags_attribute_key}`,
      useCache: true,
      // All the linked attribute's field definitions, so AttributePropertyTags can label every kind of tag
      // (checkbox printing methods, boolean capabilities, a machine's select type / colour count, ...). The
      // ordered listing_properties (which properties to show, and the formatted dimension/thickness values)
      // ride on each serialized option as option_tags_listing_properties; see resolveOptionTagListingProperties.
      callback: (data) => { setOptionTagsSubFormAttributes(data && data.create_sub_form && data.create_sub_form.attributes); }
    })
  }, []);

  // Seed the reported wastage from the section's breakdown on mount, and keep it in step with re-prices via the
  // material_wastage_recomputed event. Matched to this machine by field; the breakdown carries the raw reported
  // value in the machine's own unit.
  useEffect(() => {
    const fromBreakdown = (breakdown) => {
      const entry = (breakdown || []).find((row) => row.field === config.name);
      return entry ? { count: entry.value, unit: entry.unit } : null;
    };
    // Match this machine's OWN column by its UNIQUE key (item uuid + extra_item_reason -- the column_key
    // Frank::MaterialAutoWastage emits), not the display title: a carton_lamination section's base and extra columns
    // share the "Lamination" title, so the title cannot tell them apart (rule 7). A double printing block's carton
    // and carton_extra likewise each have their own key.
    const columnKey = item != undefined ? `${item.uuid}_${item.extra_item_reason || ''}` : undefined;
    const matchesColumn = (row) => navSection != undefined && row.label === navSection.label && (row.column_key || '') === (columnKey || '');
    const sectionEntry = (window.material_auto_wastages || []).find((row) => matchesColumn(row));
    setReportedWastage(sectionEntry ? fromBreakdown(sectionEntry.breakdown) : null);

    const onRecompute = (event) => {
      if(!matchesColumn(event.detail)) { return; }
      setReportedWastage(fromBreakdown(event.detail.breakdown));
    };
    document.addEventListener('material_wastage_recomputed', onRecompute);
    return () => { document.removeEventListener('material_wastage_recomputed', onRecompute); };
  }, []);

  const updateSelectedUnitPriceSummary = () => {
    setSelectedUnitPriceSummary('');
    for(let i = 0; i < userUnitPrices.length; i++) {
      if(parseInt(value) == parseInt(userUnitPrices[i].id)) {
        setSelectedUnitPriceSummary(userUnitPrices[i].summary);
      }
    }
  }

  const setValueWrapped = (e) => {
    let price_id = e.target.value;
    let isSystem = false;

    for(let i=0; i< systemUnitPrices.length; i++) {
      if(parseInt(price_id) == parseInt(systemUnitPrices[i].id)) {
        isSystem = true;
      }
    }
    if(isSystem) {
      setCopyFromSystemId(price_id);
      setCopyFromSystemMode(true);
    }else {
      // A material pick that would zero priced work is confirmed BEFORE it is applied, so cancelling keeps the
      // pricing intact. Sub-section resets (hot stamping, printing machine, ...) are known synchronously; the İç
      // sıvama NAV-SECTION close is a server fact (whether the new material is gray, decided across every card), so
      // the hard_cardboard material picker also asks the server first and folds that in.
      const syncImpacts = collectSectionResetImpacts(price_id);
      if(config.checks_inside_lamination_close) {
        checkInsideLaminationClose(price_id, (navSectionImpacts) => { askConfirmOrCommit([...syncImpacts, ...navSectionImpacts], price_id); });
      } else {
        askConfirmOrCommit(syncImpacts, price_id);
      }
    }
  }

  const commitSelectedValue = (price_id) => {
    setValue(config.name, price_id);
    setValue('updated_at', Date.now());
  }

  const askConfirmOrCommit = (impacts, price_id) => {
    if(impacts.length > 0) {
      AskConfirm(confirmPopupRef, buildSectionResetQuestion(impacts), () => { commitSelectedValue(price_id); }, null, { danger: true });
    } else {
      commitSelectedValue(price_id);
    }
  }

  // Ask the server whether switching THIS hard_cardboard sub-section to the prospective material would close a
  // priced İç sıvama follower (it bills only while a card is priced on a gray board -- a server decision spanning
  // every card, not on the client). Async, so the picker gates the commit on the callback. A failed check falls
  // back to no impact: a best-effort warning must never block the material change, it just degrades to the prior
  // silent behaviour.
  const checkInsideLaminationClose = (price_id, done) => {
    PostRequest({
      url: `/mcc/${window.user_quote_id}/inside-lamination-close-preview`,
      data: {
        nav_section_index: navSection != undefined ? navSection.index : undefined,
        item_uuid: item != undefined ? item.uuid : undefined,
        material_id: price_id,
      },
      callback: (data) => {
        const impacts = (data && data.closes)
          ? (data.labels || []).map((label) => ({ section: label, reason: t('quote_builder.inside_lamination_close_reason') }))
          : [];
        done(impacts);
      }
    }).catch(() => { done([]); });
  }

  // Ask every mounted section/picker whether this prospective material would reset it, BEFORE committing. Two
  // synchronous phases (both answered on window, not document -- document listeners are stubbed under jest;
  // dispatchEvent runs listeners before it returns, so results are ready immediately):
  //  Phase 1 -- sections report a section-disable reset (hot stamping, cellophane, ...) AND publish their
  //    prospective mirror values (material_printing_types, ...) into detail.prospectiveValues.
  //  Phase 2 -- pickers use those prospective values to report a picker-filter reset (e.g. the printing machine
  //    whose printing_type the new material no longer supports).
  // Only publishing material pickers ask, and only for a real own row -- a system / new / not-available choice
  // carries no material capabilities.
  const collectSectionResetImpacts = (price_id) => {
    if(!config.publish_item_on_change || config.publish_item_key == undefined) { return []; }
    const row = allUserUnitPrices.find((userUnitPrice) => String(userUnitPrice.id) === String(price_id));
    if(row == undefined) { return []; }

    const scope = {
      publishItemKey: config.publish_item_key,
      navSectionIndex: navSection != undefined ? navSection.index : undefined,
      itemUuid: item != undefined ? item.uuid : undefined,
      // is_item_extra + extra_item_reason distinguish an extra-item column from the base column that shares its
      // uuid -- part of what sections.yml `if: sameItem()` compares, so responders scope on them too.
      isItemExtra: item != undefined ? item.is_item_extra : undefined,
      extraItemReason: item != undefined ? item.extra_item_reason : undefined,
    };

    const sectionPhase = { ...scope, changedUserUnitPrice: row, impacts: [], prospectiveValues: {} };
    window.dispatchEvent(new CustomEvent('quote_builder.section_reset_impact_query', { detail: sectionPhase }));

    const pickerPhase = { ...scope, prospectiveValues: sectionPhase.prospectiveValues, impacts: [] };
    window.dispatchEvent(new CustomEvent('quote_builder.picker_reset_impact_query', { detail: pickerPhase }));

    return [...sectionPhase.impacts, ...pickerPhase.impacts];
  }

  const buildSectionResetQuestion = (impacts) => (
    <div className="q-reset-confirm">
      <i className="fa fa-exclamation-triangle q-reset-confirm__icon" aria-hidden="true"></i>
      <div className="q-reset-confirm__body">
        <div className="q-reset-confirm__title">{t('quote_builder.section_reset_confirm_title')}</div>
        {impacts.length === 1 ? (
          <div className="q-reset-confirm__line">{t('quote_builder.section_reset_confirm_line', { section: impacts[0].section, reason: impacts[0].reason })}</div>
        ) : (
          <ul className="q-reset-confirm__list">
            {impacts.map((impact, index) => (
              <li key={index}>{t('quote_builder.section_reset_confirm_line', { section: impact.section, reason: impact.reason })}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )

  const filteredBy = () => {
    if(config.filter_wording_eval != undefined) {
      return t('quote_builder.my_unit_prices') + ' | ' +  t( 'quote_builder.' + eval(config.filter_wording_eval)) + ' ' + t('quote_builder.unit_prices')
    }

    if(config.filter_wording != undefined) {
      return t('quote_builder.my_unit_prices') + ' | ' +  t('quote_builder.' + config.filter_wording)
    }
  }

  const filteredNumbers = () => {
    return t('quote_builder.showing_filtered_numbers').replace('%{showingNumber}', userUnitPrices.length).replace('%{all}', allUserUnitPrices.length)
  }

  // list_filtered_out_results_with_reasons (sections.yml, opt-in per picker): every own price the
  // filters did NOT keep is listed as a disabled row carrying the FIRST reason it did not make the cut
  // (each filters clause can carry a reason translation key; clauses without one fall back to the
  // picker's filter wording), so the user can always see WHY a machine is missing -- not only when the
  // filters leave zero selectable prices. Opt-in, because every enabled picker needs its reasons
  // written. Evaluated at render time so the reasons always use the current form values.
  const listFilteredOutResultsWithReasons = () => {
    return config.list_filtered_out_results_with_reasons == true &&
      (config.filter != undefined || config.filters != undefined);
  }

  // The own prices the filters dropped -- everything fetched minus what stayed selectable. userUnitPrices
  // is a subset of allUserUnitPrices (same rows), so identity by id is exact and also catches the rows the
  // filter dropped by failing CLOSED on a clause that threw.
  const filteredOutUnitPrices = () => {
    const availableIds = new Set(userUnitPrices.map((price) => price.id));
    return allUserUnitPrices.filter((price) => !availableIds.has(price.id));
  }

  const firstFilteredOutReason = (user_unit_price) => {
    const filterClauses = config.filters != undefined ? config.filters : [{ filter: config.filter }];
    for(let i = 0; i < filterClauses.length; i++) {
      let clausePasses = true;
      try { clausePasses = eval(filterClauses[i].filter); } catch(evaluationError) { clausePasses = false; }
      if(!clausePasses) {
        return filterClauses[i].reason != undefined ? t(filterClauses[i].reason) : fallbackFilteredOutReason();
      }
    }
    return fallbackFilteredOutReason();
  }

  const fallbackFilteredOutReason = () => {
    return config.filter_wording != undefined ? t('quote_builder.' + config.filter_wording) : '';
  }

  // The reason this option would be filtered out under a GIVEN values object, or undefined if it passes every
  // clause. Same clause + reason logic as firstFilteredOutReason, but v() reads the values passed in -- so the
  // section-reset handshake can test the selection against the PROSPECTIVE material (UserUnitPrice's own filters
  // read v('material_printing_types') etc., which the changed material would move).
  const filterClauseFailureReason = (user_unit_price, valuesForEval) => {
    const v = (key) => { return valuesForEval[key] }; // eslint-disable-line no-unused-vars -- read by eval(clause)
    const filterClauses = config.filters != undefined ? config.filters : (config.filter != undefined ? [{ filter: config.filter }] : []);
    for(let i = 0; i < filterClauses.length; i++) {
      let passes = true;
      try { passes = eval(filterClauses[i].filter); } catch(evaluationError) { passes = false; }
      if(!passes) { return filterClauses[i].reason != undefined ? t(filterClauses[i].reason) : fallbackFilteredOutReason(); }
    }
    return undefined;
  }

  // Phase 2 of the section-reset handshake: report this picker when the prospective material (phase 1's published
  // mirror values) would filter out its CURRENT selection -- e.g. a material whose printing types no longer include
  // the picked printing machine's type. Reports only when the picker filters on a value the change moved, has a real
  // selection now, and is currently valid but prospectively excluded -- scoped by sameItem, as the mirror is.
  useEffect(() => {
    const hasFilters = config.filter != undefined || config.filters != undefined;
    if(!hasFilters || config.refilter_on_value_change == undefined) { return; }

    const onPickerImpactQuery = (event) => {
      const detail = event.detail || {};
      if(!Array.isArray(detail.impacts) || detail.prospectiveValues == undefined) { return; }
      // Scope as the material mirror does (sections.yml `if: sameItem()`): same nav section, same item uuid, AND
      // same is_item_extra / extra_item_reason -- an extra-item column reuses the base item's uuid, so those two
      // are what tell a base column's change apart from an extra column's.
      if(navSection == undefined || navSection.index !== detail.navSectionIndex) { return; }
      if(item == undefined || detail.itemUuid == undefined || item.uuid !== detail.itemUuid) { return; }
      if((item.is_item_extra || false) !== (detail.isItemExtra || false)) { return; }
      if((item.extra_item_reason || null) !== (detail.extraItemReason || null)) { return; }
      // Only if the value this picker refilters on is one the material change actually moves.
      if(!(config.refilter_on_value_change in detail.prospectiveValues)) { return; }
      const currentValue = valueRef.current;
      if(currentValue == undefined || currentValue == VALUE_NOT_AVAILABLE || currentValue == VALUE_EMPTY || currentValue == VALUE_NEW) { return; }

      const selected = allUserUnitPricesRef.current.find((userUnitPrice) => String(userUnitPrice.id) === String(currentValue));
      if(selected == undefined) { return; }

      const prospectiveValues = { ...valuesRef.current, ...detail.prospectiveValues };
      if(filterClauseFailureReason(selected, valuesRef.current) != undefined) { return; } // already excluded -> nothing new
      const reason = filterClauseFailureReason(selected, prospectiveValues);
      if(reason != undefined) {
        detail.impacts.push({
          section: config.label != undefined ? t(config.label) : selected.description,
          reason: reason,
        });
      }
    };

    window.addEventListener('quote_builder.picker_reset_impact_query', onPickerImpactQuery);
    return () => { window.removeEventListener('quote_builder.picker_reset_impact_query', onPickerImpactQuery); };
  }, [])

  const filteredOutOptionLabel = (user_unit_price) => {
    const filteredOutReason = firstFilteredOutReason(user_unit_price);
    if (filteredOutReason == undefined || filteredOutReason == '') { return user_unit_price.description; }
    return `${user_unit_price.description} - ${filteredOutReason}`;
  }

  const callRequestPublishItemOnChange = () => {
    if(config.publish_item_on_change && config.publish_item_if_eval == undefined) {
      requestPublishItemOnChange({ publish_item_key: config.publish_item_key });
    }

    if(config.publish_item_on_change && config.publish_item_if_eval != undefined && eval(config.publish_item_if_eval) ) {
      requestPublishItemOnChange({ publish_item_key: config.publish_item_key });
    }
  }

  useEffect(() => {
    callRequestPublishItemOnChange();

    if(config.get_extra_items_on_change_if == undefined && config.get_extra_items_on_change) {
      requestGetExtraItems( { key: config.key, value: value } );
    }

    if(config.get_extra_items_on_change_if != undefined && eval(config.get_extra_items_on_change_if)) {
      requestGetExtraItems( { key: config.key, value: value } );
    }

    if(config.not_available_if != undefined && eval(config.not_available_if)) {
      setNotAvailableReason(t(config.not_available_if_reason));
    } else {
      setNotAvailableReason('');
    }

  }, [value])


  if(config.listen_conditions != undefined) {
    for(let i=0; i < config.listen_conditions.length; i++) {

      useEffect(() => {
        let listener = ListenCondition.listener({
          config: config,
          condition: config.listen_conditions[i],
          item: item,
          navSection: navSection,
          setValue: setValue,
          GetRequest: GetRequest,
          setReadOnly: setReadOnly,
          setReadOnlyMessage: setReadOnlyMessage,
          setNotAvailable: setNotAvailable,
          setNotAvailableReason: setNotAvailableReason,
          options: options,
          navSectionColumnType: navSectionColumnType,
          fetchUserUnitPrices: fetchUserUnitPrices,
          fetchSystemUnitPrices: fetchSystemUnitPrices,
          checkForValueInUserPricesRequired: checkForValueInUserPricesRequired,
        });

        document.addEventListener(config.listen_conditions[i].listen, listener);
        return () => { document.removeEventListener(config.listen_conditions[i].listen, listener);}

      }, []);
    }
  }

  //create from system start
  const [systemUnitPrices, setSystemUnitPrices] = useState([]);
  const [copyFromSystemId, setCopyFromSystemId] = useState(undefined);

  const default_system_list_path = '/quote-builder/system_user_unit_prices/list/settings_list/:key/:lng';
  const [createBtnEnabled, setCreateBtnEnabled] = useState('disabled');
  const [createFromSystemEnabled, setCreateFromSystemEnabled] = useState(false);

  const fetchSystemUnitPrices = (filter_eval) => {
    GetRequest({
      url: default_system_list_path.replace(':key', config.key).replace(':lng', window.locale),
      callback: (data) => {
        data = filteredData(data, filter_eval);
        setSystemUnitPrices(data);
        setCreateFromSystemEnabled(() => { return data.length > 0});
        setCreateBtnEnabled(() => { return '' });
      }
    })
  }

  useEffect(fetchSystemUnitPrices, []);
 //create from system end

  if(notAvailableReason != undefined && notAvailableReason != '') {
    return <tr>
      <td colSpan="2">
        <div className="well">
          <i className="fa fa-exclamation-circle"></i>  <small> {notAvailableReason} </small>
        </div>
      </td>
    </tr>;
  }

  const myUnitPricesLabel = () => {
    let label = (config.filter == undefined && config.filters == undefined) ? t('quote_builder.my_unit_prices') : filteredBy();
    if(allUserUnitPrices.length != userUnitPrices.length) { label = label + ' | ' + filteredNumbers()}
    return label
  }

  // Selecting an option in the custom tag dropdown routes through the same handler the native <select>'s
  // onChange used, so a system price still copies-from-system and 'new-attribute' still opens the create
  // popup -- the dropdown changes how options look, never what a selection does.
  const onSelectValue = (selectedValue) => {
    setValueWrapped({ target: { value: selectedValue } });
  }

  // One own-price option's label -- an optional [name] prefix, the description, and (for a non-system price)
  // a ", price" suffix. The dropdown list shows the full label; the collapsed selected value passes
  // withPrice:false to drop the price tail, which is already shown in the summary line below the picker.
  const ownPriceOptionLabel = (userUnitPrice, { withPrice = true } = {}) => {
    const namePrefix = (userUnitPrice.name != '' && userUnitPrice.name != undefined) ? `[${userUnitPrice.name}] ` : '';
    const priceSuffix = (withPrice && !userUnitPrice.is_system && userUnitPrice.price_with_currency_formatted != '' && userUnitPrice.price_with_currency_formatted != undefined) ? `, ${userUnitPrice.price_with_currency_formatted}` : '';
    return `${namePrefix}${userUnitPrice.description}${priceSuffix}`;
  }

  // Which properties the option tags show, and in what order. Machines (and carton) declare this list
  // server-side -- it rides on each serialized option as option_tags_listing_properties and includes the
  // computed dimension/thickness values. A material type with no explicit list falls back to its checkbox
  // (printing methods) and boolean (capabilities) fields, so those pickers keep showing their tags.
  const resolveOptionTagListingProperties = () => {
    const priceWithListing = allUserUnitPrices.concat(systemUnitPrices)
      .find((price) => price.option_tags_listing_properties && price.option_tags_listing_properties.length > 0);
    if(priceWithListing) { return priceWithListing.option_tags_listing_properties; }
    if(optionTagsSubFormAttributes == undefined) { return undefined; }
    return optionTagsSubFormAttributes
      .filter((field) => field.form_type === 'checkboxes' || field.form_type === 'boolean')
      .map((field) => field.name);
  }

  // The grouped option model for UserUnitPriceTagSelect -- the same groups, order and gating the native
  // <select> rendered (empty / not-available / my unit prices with filtered-out rows / most commonly used /
  // add-new). Each own and system option carries option_tags_properties so the dropdown renders its tags.
  const buildTagSelectGroups = () => {
    const groups = [];

    // The empty / not-available rows are the "no selection" placeholder, not a price -- flagged so the
    // dropdown styles them apart (a divider + muted italic) so they read as "clear the selection" and are
    // not mistaken for, or mis-clicked in place of, a real price.
    if(config.empty_option_enabled) {
      groups.push({ key: 'empty', options: [{ key: 'empty', value: VALUE_EMPTY, label: '', placeholder: true }] });
    }

    if(config.not_available_enabled) {
      groups.push({ key: 'not-available', options: [{ key: 'not-available', value: VALUE_NOT_AVAILABLE, label: t('quote_builder.not_included'), placeholder: true }] });
    }

    if(allUserUnitPrices.length > 0) {
      const ownOptions = [];
      if(listFilteredOutResultsWithReasons()) {
        filteredOutUnitPrices().forEach((userUnitPrice) => {
          ownOptions.push({ key: `filtered-out-${userUnitPrice.id}`, value: VALUE_EMPTY, label: filteredOutOptionLabel(userUnitPrice), disabled: true });
        });
      }
      userUnitPrices.forEach((userUnitPrice) => {
        ownOptions.push({
          key: `own-${userUnitPrice.id}`,
          value: userUnitPrice.id,
          label: ownPriceOptionLabel(userUnitPrice),
          // The collapsed selected value drops the price tail when it is already shown in the summary line
          // below the picker (view_selected_summary), so the price is not repeated in both places.
          selectedLabel: config.view_selected_summary ? ownPriceOptionLabel(userUnitPrice, { withPrice: false }) : undefined,
          tagsProperties: userUnitPrice.option_tags_properties,
        });
      });
      groups.push({ key: 'mine', label: myUnitPricesLabel(), options: ownOptions });
    }

    if(systemUnitPrices.length > 0) {
      groups.push({
        key: 'system',
        label: t('quote_builder.optgroup_system_prices'),
        options: systemUnitPrices.map((userUnitPrice) => ({
          key: `system-${userUnitPrice.id}`,
          value: userUnitPrice.id,
          label: userUnitPrice.description_for_system,
          tagsProperties: userUnitPrice.option_tags_properties,
        })),
      });
    }

    if(config.create_enabled) {
      groups.push({ key: 'new', options: [{ key: 'new', value: VALUE_NEW, label: t(config.create_cta), disabled: createBtnEnabled === 'disabled' }] });
    }

    return groups;
  }

  // Inline required-field feedback. A UserUnitPrice picker manages its value through setValue, not
  // react-hook-form's register, so it never joins the `errors` object -- a section-level disabled_if
  // silently blocks Save with no per-field cue. When the field carries a validator_eval (settings.yml),
  // evaluate it here (same convention as SelectFormField) and, on failure, mark the select with the same
  // input-error border + `error` message the registered fields use. Fail-open on a throw: a config bug
  // must not surface a spurious error while Save stays gated by disabled_if anyway.
  const validationErrorMessage = () => {
    if(config.validator_eval == undefined || disabled) { return undefined; }
    let passes;
    try { passes = eval(config.validator_eval); } catch(evaluationError) { passes = true; }
    return passes ? undefined : t(`quote_builder.${config.validator_eval_message}`);
  }
  const validationError = validationErrorMessage();
  return (
    <tr>
      <td>
        {capitalizeFirstLetter(`${config.label != undefined ? t(config.label) : ''}${config.label_translated != undefined ? config.label_translated : ''}${config.hide_price_label_suffix ? '' : ` ${t('quote_builder.price')}`}`)}
        {config.helper_text != undefined && (
          <><br /><small className="form-text text-muted" dangerouslySetInnerHTML={{ __html: t(config.helper_text) }} /></>
        )}
      </td>
      <td align="right">
        <ConfirmPopup myRef={confirmPopupRef} />

        {customSelectStyleEnabled ? (
          <UserUnitPriceTagSelect
            groups={buildTagSelectGroups()}
            value={value}
            onSelect={onSelectValue}
            disabled={readOnly || window.user_quote_finalised}
            notAvailable={notAvailable}
            subFormAttributes={optionTagsSubFormAttributes}
            listingProperties={resolveOptionTagListingProperties()}
            placeholder={config.not_available_enabled ? t('quote_builder.not_included') : ''}
          />
        ) : (
        <select value={value}
          onChange={setValueWrapped}
          onFocus={refetchListOnFocusIfLive}
          name={config.name}
          disabled={readOnly || window.user_quote_finalised }
          // Price labels carry a full description (name, dimensions, price); an explicit width caps the
          // select to its column so a long option truncates instead of growing the select past the section edge.
          style={{ width: '100%', maxWidth: '80%' }}
          className={`form-control ${(notAvailable) ? 'disabledForm' : ''} ${validationError ? 'input-error' : ''}`}
        >
          {config.empty_option_enabled && <optgroup label=""> <option key={VALUE_EMPTY} value={VALUE_EMPTY}> </option> </optgroup> }
          {config.not_available_enabled && <optgroup label=""> <option key="n/a" value={VALUE_NOT_AVAILABLE}> {t('quote_builder.not_included')} </option> </optgroup> }

          {allUserUnitPrices.length > 0 && <optgroup label={myUnitPricesLabel()}>
            {listFilteredOutResultsWithReasons() && filteredOutUnitPrices().map((userUnitPrice) => {
              return <option disabled key={`filtered-out-${userUnitPrice.id}`} value={VALUE_EMPTY}>
                {filteredOutOptionLabel(userUnitPrice)}
              </option>
            })}
            {userUnitPrices.map((userUnitPrice) => {
              return <option
                key={userUnitPrice.id}
                data-is-system="false"
                value={userUnitPrice.id}> { userUnitPrice.name != '' && userUnitPrice.name !=  undefined && `[${userUnitPrice.name}]`}
                {userUnitPrice.description}{!userUnitPrice.is_system && userUnitPrice.price_with_currency_formatted != '' && userUnitPrice.price_with_currency_formatted != undefined && `, ${userUnitPrice.price_with_currency_formatted}`}
              </option>
            })}
          </optgroup>
          }

          {systemUnitPrices.length > 0  && (<optgroup label={t('quote_builder.optgroup_system_prices')}>

            {systemUnitPrices.map((userUnitPrice) => {
              return <option
                key={userUnitPrice.id}
                data-is-system="true"
                value={userUnitPrice.id}>
                {userUnitPrice.description_for_system}
              </option>
            })}
          </optgroup>)}



          {config.create_enabled && <optgroup label=""> <option key="new" value={VALUE_NEW} disabled={createBtnEnabled}> {t(config.create_cta)} </option> </optgroup> }
        </select>
        )}

        {validationError && <><small className="error"> {validationError} </small><br /></>}

        {config.view_selected_summary && selectedUnitPriceSummary != '' && (
          <span>
            <small>
              <div dangerouslySetInnerHTML={{__html: selectedUnitPriceSummary}}></div>
            </small>
            {reportedWastage != null && (
              <small><div>{t('quote_builder.reported_wastage_used', { count: reportedWastage.count, unit: t('quote_builder.wastage_unit_' + reportedWastage.unit) })}</div></small>
            )}
            {!window.user_quote_finalised && config.edit_selected_item_enabled && (<> &nbsp; <small> <a href="" onClick={handleEdit}> <i className="fa fa-pencil-square-o" aria-hidden="true"></i>  {t('quote_builder.edit')} </a></small> <br /></>)}
          </span>
        )}

        {!window.user_quote_finalised && allUserUnitPrices.length > 0 && (
         <> <small> <a onClick={listAllItems} href="#"> <i className="fa fa-list" aria-hidden="true"></i> {t('quote_builder.show_all_items')} </a> </small> <br /> </>
        )}

        {readOnlyMessage != '' && <> <small className="q-read-only-message"> {readOnlyMessage} <br /> </small> </> }
        {/* Footer note carries only a trailing <br />, like its siblings above; a leading one would
            stack on the preceding element's trailing break into a blank line above the note. */}
        {config.footer_note != undefined && (<> <small> {t(config.footer_note)} <br /> </small></>)}
        {config.create_enabled && value == VALUE_NEW && newUserPricePopup()}
        {config.edit_selected_item_enabled && editingMode && editPricePopup()}
        {config.create_enabled && copyFromSystemMode && copyFromSystemPopup()}
        {listingMode && listingModePopup()}
      </td>
    </tr>
  )
}

export default UserUnitPrice;
